import { ComponentInstance } from "@uniformdev/canvas";
import { type ComponentProps, UniformSlot } from "@uniformdev/canvas-react";

import { IntercomProvider } from "@/libraries/intercom/src";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";
import { GlobalFooter } from "@/patterns/global-footer/src";
import GlobalNav from "@/patterns/global-nav/src";
import { EnterprisePageProductNav } from "@/templates/enterprise/enterprise-landing-page/sections";
import JiraSoftwareNav from "@/templates/software/jira/sections/nav-bar/software-jira-navbar";
import JiraServiceManagementSoftwareNavV2 from "@/templates/software/jira/service-management/jira-service-management-software-page/src/sections/software-jira-service-management-navbarv2";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { useLocale } from "../../../libraries/web-framework/src/localization/use-locale";

import { OptionalSiteSelectorContextProvider } from "./optional-contexts.tsx";

export interface PageProps extends ComponentProps {
    pageTitle: string;
    metaDescription?: string;
    siteSelectorProductKey?: string;
}

export const Page = (props: PageProps) => {
    const { component, siteSelectorProductKey } = props;

    const fullSiteSelectorProductKey =
        siteSelectorProductKey === "jira-service-management"
            ? "jira-servicedesk.ondemand"
            : `${siteSelectorProductKey}.ondemand`;

    const locale = useLocale();

    const isBootIntercom: boolean = checkFeatureFlagGate(
        featureFlags.gwp5381AddIntercomWidgetToUniform
    );

    const intercomAppId = locale === "ja" ? "p0ww63i2" : "odnoznr4";

    const contentSlot = component.slots?.["content"] ?? [];

    const pageHasGlobalNavComponent = !!(
        contentSlot as ComponentInstance[]
    ).find((content) => content.type === "globalNav");
    const pageHasGlobalFooterComponent = !!(
        contentSlot as ComponentInstance[]
    ).find((content) => content.type === "globalFooter");

    return (
        <IntercomProvider appId={intercomAppId} triggerBoot={isBootIntercom}>
            {!pageHasGlobalNavComponent && <PageNav component={component} />}
            <TypographyStyles>
                <OptionalSiteSelectorContextProvider
                    siteSelectorProductKey={fullSiteSelectorProductKey}
                >
                    <main id="content">
                        <UniformSlot name="content" />
                    </main>
                </OptionalSiteSelectorContextProvider>
            </TypographyStyles>
            {!pageHasGlobalFooterComponent && <GlobalFooter />}
        </IntercomProvider>
    );
};

const PageNav = ({ component }: ComponentProps) => {
    const pageNavType =
        component.parameters &&
        component.parameters["nav"] &&
        component.parameters["nav"].value;
    let pageNav;

    switch (pageNavType) {
        case "enterprise":
            pageNav = <EnterprisePageProductNav />;
            break;
        case "jira-software":
            pageNav = <JiraSoftwareNav />;
            break;
        case "jira-service-management":
            pageNav = <JiraServiceManagementSoftwareNavV2 />;
            break;
        case "none":
            break;
        case "global":
        default:
            pageNav = (
                <GlobalNav productKey="uniform" isShowTryNowLink={true} />
            );
            break;
    }

    return pageNav;
};

export default Page;
